import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';

// HAT-1383: initial statuses that are still waiting for approval. A request in one of them is
// approved by moving its initial status to Approved (the backend then moves every month to
// InitialApprovalApproved). PendingCompletion / CompletionReturned are left out on purpose -
// documents are missing there, so approval stays a deliberate status change.
const APPROVABLE_INITIAL_STATUSES = new Set([
  'PendingFirstReview',
  'PendingManagementApproval',
  'PendingExceptionsCommitteeApproval',
]);

// True while the request still waits for the office's decision, in either lifecycle.
const isAwaitingDecision = (request: ManagementSupportRequestDtoIn): boolean =>
  request.isInInitialStatusProcess
    ? APPROVABLE_INITIAL_STATUSES.has(String(request.initialStatus))
    : String(request.status) === 'PendingExceptionsCommitteeApproval';

const allowed = (request: ManagementSupportRequestDtoIn, target: string): string | null =>
  request.allowedNextStatuses?.includes(target) ? target : null;

/** The status "approve" moves this request to, or null when there is nothing to approve. */
export const getApproveStatus = (request: ManagementSupportRequestDtoIn): string | null => {
  if (!isAwaitingDecision(request)) return null;
  return allowed(request, request.isInInitialStatusProcess ? 'Approved' : 'InitialApprovalApproved');
};

/**
 * HAT-1383: an approved month (lifecycle 2, the "Approved" tab) moves on to PendingExecution.
 * Kept apart from approve on purpose - it is a separate step the office takes from the
 * approved-amount dialog only.
 */
export const getMoveToExecutionStatus = (request: ManagementSupportRequestDtoIn): string | null =>
  !request.isInInitialStatusProcess && String(request.status) === 'InitialApprovalApproved'
    ? allowed(request, 'PendingExecution')
    : null;

/** HAT-1391: the status "reject" moves this request to, under the same rule as approve. */
export const getRejectStatus = (request: ManagementSupportRequestDtoIn): string | null =>
  isAwaitingDecision(request) ? allowed(request, 'Rejected') : null;
